import React from "react";
import { Box, Radio, Stack, Typography } from "@mui/material";

interface RadioOptionCardProps {
  label: React.ReactNode;
  description?: React.ReactNode;
  value: string;
  selected: boolean;
  onSelect: (value: string) => void;
  /** Optional content rendered when the card is selected (e.g. inline config). */
  expandedContent?: React.ReactNode;
  /** Optional element rendered top-right (e.g. "+ config" chip). */
  rightSlot?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Selectable card with neutral / selected (purple-tinted) variants.
 * Used by L1, L2, L3 selectors and the FCR + implementer/setting radio rows.
 */
const RadioOptionCard: React.FC<RadioOptionCardProps> = ({
  label,
  description,
  value,
  selected,
  onSelect,
  expandedContent,
  rightSlot,
  disabled,
}) => {
  const handleClick = () => {
    if (!disabled) onSelect(value);
  };

  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect(value);
        }
      }}
      sx={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "purple.50" : "background.paper",
        borderRadius: 4,
        p: 4,
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": disabled ? {} : { borderColor: selected ? "primary.main" : "text.disabled" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={2}>
        <Radio
          checked={selected}
          tabIndex={-1}
          disabled={disabled}
          size="small"
          sx={{ p: 0, mt: "2px" }}
          inputProps={{ "aria-hidden": true }}
        />
        <Stack flex={1} gap={0}>
          <Typography component="h4" variant="body2" color="#1E293B">
            {label}
          </Typography>
          {description && (
            <Typography component="p" variant="caption" color="text.secondary" lineHeight="143%">
              {description}
            </Typography>
          )}
        </Stack>
        {rightSlot}
      </Stack>
      {selected && expandedContent && <Box mt={4}>{expandedContent}</Box>}
    </Box>
  );
};

export default RadioOptionCard;
